import React from 'react';

interface MatrixRow {
  groupId: string;
  groupName: string;
  data: { customerId: string; customerName: string; count: number }[];
  rowTotal: number;
}

interface ColumnTotal {
  customerId: string;
  customerName: string;
  total: number;
}

interface ServiceRequestMatrixTableProps {
  matrix: MatrixRow[];
  columnTotals: ColumnTotal[];
  grandTotal: number;
  maxCount: number;
  onCellClick?: (customerId: string, groupId: string, customerName: string, groupName: string) => void;
}

// Get cell background color based on value (heatmap)
const getCellColor = (count: number, maxCount: number): string => {
  if (count === 0) return 'bg-gray-50';
  const intensity = count / maxCount;
  if (intensity <= 0.25) return 'bg-blue-100 text-blue-800';
  if (intensity <= 0.5) return 'bg-yellow-200 text-yellow-900';
  if (intensity <= 0.75) return 'bg-orange-300 text-orange-900';
  return 'bg-red-400 text-white';
};

const ServiceRequestMatrixTable: React.FC<ServiceRequestMatrixTableProps> = ({
  matrix,
  columnTotals,
  grandTotal,
  maxCount,
  onCellClick,
}) => {
  if (matrix.length === 0 || columnTotals.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Service Requests by Customer and Assignment Group
        </h3>
        <div className="h-32 flex items-center justify-center text-gray-500">
          No data available for the selected filters
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-700 rounded-t-lg">
        <h3 className="text-lg font-medium text-white text-center">
          Service Requests by Customer and Assignment Group
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-100 z-10 border-r border-gray-300">
                Assignment Group
              </th>
              {columnTotals.map((col) => (
                <th
                  key={col.customerId}
                  className="px-2 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-200"
                  title={col.customerName}
                >
                  {col.customerName.length > 15
                    ? col.customerName.substring(0, 15) + '...'
                    : col.customerName}
                </th>
              ))}
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider bg-gray-200">
                Count
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {matrix.map((row) => (
              <tr key={row.groupId} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 sticky left-0 bg-white z-10 border-r border-gray-300">
                  {row.groupName}
                </td>
                {row.data.map((cell) => (
                  <td
                    key={`${row.groupId}-${cell.customerId}`}
                    className={`px-2 py-2 text-center text-sm font-medium border-r border-gray-200 ${getCellColor(
                      cell.count,
                      maxCount
                    )} ${cell.count > 0 ? 'cursor-pointer hover:opacity-80' : ''}`}
                    onClick={() => {
                      if (cell.count > 0 && onCellClick) {
                        onCellClick(cell.customerId, row.groupId, cell.customerName, row.groupName);
                      }
                    }}
                    title={cell.count > 0 ? `Click to view ${cell.count} tickets for ${cell.customerName} - ${row.groupName}` : undefined}
                  >
                    {cell.count > 0 ? (
                      <span className="underline decoration-dotted">{cell.count}</span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                ))}
                <td className="px-3 py-2 text-center text-sm font-bold text-gray-900 bg-gray-100">
                  {row.rowTotal}
                </td>
              </tr>
            ))}

            {/* Column totals row */}
            <tr className="bg-gray-100 font-bold">
              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 sticky left-0 bg-gray-100 z-10 border-r border-gray-300">
                Count
              </td>
              {columnTotals.map((col) => (
                <td
                  key={`total-${col.customerId}`}
                  className="px-2 py-2 text-center text-sm text-gray-900 border-r border-gray-200"
                >
                  {col.total}
                </td>
              ))}
              <td className="px-3 py-2 text-center text-sm font-bold text-white bg-gray-700">
                {grandTotal}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServiceRequestMatrixTable;
